import { useEffect, useRef } from 'react';
import {
  setAuthErrorHandler,
  setAuthRefreshHandler,
  setAuthRequestTokenProvider,
  setAuthTokenProvider,
} from '@/shared/data/client';
import { emitAuthDiagnostic } from './authDiagnostics';
import { getE2eAccessToken } from './sessionStorage';
import {
  isIdentityMismatchError,
  isMissingUserIdentityError,
  isUserNotFoundError,
  resolveTokenFromClerkClientSessions,
} from './useAuth.helpers';
import type { AuthSession } from './types';
import type { RefreshOutcome } from './useAuth.helpers';

const isAuthEntryPath = (pathname: string): boolean => {
  return (
    pathname.startsWith('/sign-in') ||
    pathname.startsWith('/sign-up') ||
    pathname.startsWith('/accept-invitation')
  );
};

type UseAuthClientBindingsArgs = {
  clerkClientRef: { current: unknown };
  clerkGetTokenRef: {
    current: (options?: { skipCache?: boolean }) => Promise<string | null>;
  };
  clearAuthRecoveryFailures: () => void;
  expireSession: (reason: 'timer' | 'refresh_error' | 'session_expired_event') => void;
  loading: boolean;
  runRefreshSessionRef: { current: (opts?: { force?: boolean }) => Promise<RefreshOutcome> };
  sessionRef: { current: AuthSession | null };
};

type CachedClerkRequestToken = {
  refreshedAtMs: number;
  refreshedWithSkipCache: boolean;
  token: string;
  usableUntilMs: number;
};

type ClerkRequestTokenInFlight = {
  promise: Promise<string | null>;
  skipCache: boolean;
};

const clerkRequestTokenRefreshWindowMs = 15_000;
const clerkRequestTokenUnknownExpiryCacheMs = 30_000;
const clerkForcedRequestTokenBurstCacheMs = 5_000;

const decodeBase64Url = (value: string): string | null => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padLength = (4 - (normalized.length % 4)) % 4;
  const padded = `${normalized}${'='.repeat(padLength)}`;
  try {
    return globalThis.atob(padded);
  } catch {
    return null;
  }
};

const parseJwtExpiryMs = (token: string): number | null => {
  const segments = token.split('.');
  if (segments.length !== 3) return null;
  const payloadSegment = segments[1];
  if (!payloadSegment) return null;
  const decoded = decodeBase64Url(payloadSegment);
  if (!decoded) return null;
  try {
    const parsed: unknown = JSON.parse(decoded);
    if (!parsed || typeof parsed !== 'object') return null;
    const exp = (parsed as { exp?: unknown }).exp;
    return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1000 : null;
  } catch {
    return null;
  }
};

const buildCachedClerkRequestToken = (token: string, now: number): CachedClerkRequestToken => {
  const expiryMs = parseJwtExpiryMs(token);
  return {
    refreshedAtMs: now,
    refreshedWithSkipCache: false,
    token,
    usableUntilMs:
      expiryMs === null
        ? now + clerkRequestTokenUnknownExpiryCacheMs
        : Math.max(now, expiryMs - clerkRequestTokenRefreshWindowMs),
  };
};

const isMatchingClerkRequestTokenInFlight = (
  ref: { current: ClerkRequestTokenInFlight | null },
  promise: Promise<string | null>,
): boolean => ref.current?.promise === promise;

const clearMatchingClerkRequestTokenInFlight = (
  ref: { current: ClerkRequestTokenInFlight | null },
  promise: Promise<string | null>,
): void => {
  if (isMatchingClerkRequestTokenInFlight(ref, promise)) {
    ref.current = null;
  }
};

const canUseCachedClerkRequestToken = (
  cachedToken: CachedClerkRequestToken | null,
  now: number,
  skipCache: boolean,
): cachedToken is CachedClerkRequestToken => {
  if (!cachedToken || cachedToken.usableUntilMs <= now) {
    return false;
  }
  if (!skipCache) {
    return true;
  }
  return (
    cachedToken.refreshedWithSkipCache &&
    cachedToken.refreshedAtMs + clerkForcedRequestTokenBurstCacheMs > now
  );
};

export const useAuthClientBindings = ({
  clerkClientRef,
  clerkGetTokenRef,
  clearAuthRecoveryFailures,
  expireSession,
  loading,
  runRefreshSessionRef,
  sessionRef,
}: UseAuthClientBindingsArgs): void => {
  const cachedClerkRequestTokenRef = useRef<CachedClerkRequestToken | null>(null);
  const clerkRequestTokenInFlightRef = useRef<ClerkRequestTokenInFlight | null>(null);

  useEffect(() => {
    setAuthTokenProvider(() => {
      const e2eToken = getE2eAccessToken();
      if (e2eToken) return e2eToken;
      if (loading) return null;
      return sessionRef.current?.tokens.accessToken ?? null;
    });
    setAuthRequestTokenProvider(async (input) => {
      const e2eToken = getE2eAccessToken();
      if (e2eToken) return e2eToken;
      if (loading) {
        cachedClerkRequestTokenRef.current = null;
        clerkRequestTokenInFlightRef.current = null;
        return null;
      }
      const skipCache = input?.skipCache === true;
      const now = Date.now();
      const cachedToken = cachedClerkRequestTokenRef.current;
      if (canUseCachedClerkRequestToken(cachedToken, now, skipCache)) {
        return cachedToken.token;
      }
      const inFlight = clerkRequestTokenInFlightRef.current;
      if (inFlight && (!skipCache || inFlight.skipCache)) {
        return await inFlight.promise;
      }
      const promise = (async (): Promise<string | null> => {
        const token =
          (await clerkGetTokenRef.current(input)) ??
          (await resolveTokenFromClerkClientSessions(clerkClientRef.current, input));
        return token ?? sessionRef.current?.tokens.accessToken ?? null;
      })();
      clerkRequestTokenInFlightRef.current = { promise, skipCache };
      let token: string | null;
      try {
        token = await promise;
        if (isMatchingClerkRequestTokenInFlight(clerkRequestTokenInFlightRef, promise)) {
          if (token) {
            cachedClerkRequestTokenRef.current = {
              ...buildCachedClerkRequestToken(token, Date.now()),
              refreshedWithSkipCache: skipCache,
            };
          } else {
            cachedClerkRequestTokenRef.current = null;
          }
        }
      } finally {
        clearMatchingClerkRequestTokenInFlight(clerkRequestTokenInFlightRef, promise);
      }
      if (token) {
        return token;
      }
      return sessionRef.current?.tokens.accessToken ?? null;
    });
    setAuthErrorHandler(async ({ error: authError, status }) => {
      const pathname = typeof window === 'undefined' ? '' : window.location.pathname;
      if (
        status === 401 &&
        (isMissingUserIdentityError(authError) || isUserNotFoundError(authError)) &&
        isAuthEntryPath(pathname)
      ) {
        emitAuthDiagnostic('auth_error_handler_skipped_on_auth_entry', {
          status,
          pathname,
          code: authError.code ?? null,
          message: authError.message,
          correlationId: authError.correlationId ?? null,
          errorId: authError.errorId ?? null,
          userId: sessionRef.current?.userId ?? null,
        });
        return false;
      }
      const outcome = await runRefreshSessionRef.current({ force: true });
      if (outcome.didRefresh) {
        clearAuthRecoveryFailures();
        return true;
      }
      if (
        status === 401 &&
        (isIdentityMismatchError(authError) ||
          isMissingUserIdentityError(authError) ||
          isUserNotFoundError(authError))
      ) {
        emitAuthDiagnostic('auth_error_handler_forced_expire', {
          status,
          code: authError.code ?? null,
          message: authError.message,
          correlationId: authError.correlationId ?? null,
          errorId: authError.errorId ?? null,
          userId: sessionRef.current?.userId ?? null,
        });
        expireSession('refresh_error');
      }
      return false;
    });
    setAuthRefreshHandler(async (opts) => {
      const outcome = await runRefreshSessionRef.current({ force: opts?.force });
      return outcome.didRefresh;
    });
    return () => {
      setAuthRefreshHandler(undefined);
      setAuthErrorHandler(undefined);
      setAuthRequestTokenProvider(undefined);
      setAuthTokenProvider(undefined);
    };
  }, [
    clearAuthRecoveryFailures,
    clerkClientRef,
    clerkGetTokenRef,
    expireSession,
    loading,
    runRefreshSessionRef,
    sessionRef,
  ]);
};
